import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Screen } from '@/components/Screen'
import { Button } from '@/components/Button'
import { Field, TextArea } from '@/components/Field'
import { Kicker } from '@/components/Kicker'
import { useAppStore } from '@/stores/appStore'
import { useEarlyCareState, useFocusedPerson } from '@/hooks/useFocusedPerson'
import { PACK_COLORS } from '@/design-system/tokens'
import { absoluteTime, durationBetween, relativeTime, volumeString } from '@/lib/format'
import { emitEvent } from '@/lib/emitEvent'

/**
 * HandoffScreen — the moment you take over care from your partner.
 * Shows current state (sleep status, last feed, last diaper, meds today) + a "taking over now" button
 * that logs a family.handoff.created event so the timeline has a marker.
 */
export function HandoffScreen() {
  const nav = useNavigate()
  const user = useAppStore((s) => s.user)
  const familyId = useAppStore((s) => s.familyId)
  const person = useFocusedPerson()
  const state = useEarlyCareState(person?.id ?? null)

  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  if (!person || !familyId || !user) return <Screen title="Handoff" back />

  const packColor = PACK_COLORS.school.light
  const isSleeping = state?.sleep_status === 'sleeping'

  async function takeOver() {
    setBusy(true); setErr(null)
    try {
      if (!person || !familyId || !user) return
      await emitEvent({
        familyId,
        personId: person.id,
        userId: user.id,
        eventType: 'family.handoff.created',
        payload: {
          taking_over: true,
          ...(note.trim() ? { note: note.trim() } : {}),
        } as import('@/lib/types').JSONValue,
      })
      nav('/', { replace: true })
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Screen title="Handoff" back>
      <div className="px-5 py-5 space-y-5">
        <div
          className="rounded-r-sm p-5"
          style={{ background: `linear-gradient(180deg, ${packColor}18, transparent), #141B19`, borderTop: `1px solid ${packColor}55` }}
        >
          <Kicker color={packColor}>Right now for {person.display_name}</Kicker>
          <div className="mt-3 font-serif italic text-hero text-text-primary">
            {isSleeping ? `Sleeping · ${durationBetween(state!.sleep_started_at!, null)}` : 'Awake'}
          </div>
          {isSleeping && (
            <div className="mt-1 text-caption text-text-tertiary">
              Started {absoluteTime(state?.sleep_started_at)} · {relativeTime(state?.sleep_started_at)}
            </div>
          )}
        </div>

        <section>
          <Kicker>Feed</Kicker>
          <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle p-4">
            {state?.last_feed_at ? (
              <>
                <div className="text-body text-text-primary">
                  Last: {state.last_feed_type ?? 'feed'} · {state.last_feed_volume_ml != null ? volumeString(state.last_feed_volume_ml) : '—'}
                </div>
                <div className="mt-1 text-caption text-text-tertiary">
                  {absoluteTime(state.last_feed_at)} · {relativeTime(state.last_feed_at)} · {state.feed_count_24h} feeds today
                </div>
              </>
            ) : (
              <div className="text-body text-text-tertiary">No feed logged today</div>
            )}
          </div>
        </section>

        <section>
          <Kicker>Diaper</Kicker>
          <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle p-4">
            {state?.last_diaper_at ? (
              <>
                <div className="text-body text-text-primary">Last: {state.last_diaper_type ?? '—'}</div>
                <div className="mt-1 text-caption text-text-tertiary">
                  {absoluteTime(state.last_diaper_at)} · {relativeTime(state.last_diaper_at)} · {state.wet_count_24h} wet, {state.dirty_count_24h} dirty today
                </div>
              </>
            ) : (
              <div className="text-body text-text-tertiary">No diaper logged today</div>
            )}
          </div>
        </section>

        {state?.last_medication_at && (
          <section>
            <Kicker color={PACK_COLORS.safety.light}>Medication today</Kicker>
            <div
              className="mt-2 rounded-r-sm border p-4"
              style={{ background: PACK_COLORS.safety.light + '10', borderColor: PACK_COLORS.safety.light + '33' }}
            >
              <div className="text-body text-text-primary">{state.last_medication_name}</div>
              <div className="mt-1 text-caption text-text-tertiary">
                {absoluteTime(state.last_medication_at)} · {relativeTime(state.last_medication_at)} · confirm before giving another dose
              </div>
            </div>
          </section>
        )}

        <Field label="Note for the person handing off (optional)">
          <TextArea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="'Fussy after 4pm feed', 'One green diaper', etc."
          />
        </Field>

        {err && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">
            {err}
          </div>
        )}

        <Button variant="primary" size="lg" packColor={packColor} loading={busy} className="w-full" onClick={takeOver}>
          I'm taking over now
        </Button>
      </div>
    </Screen>
  )
}
